import React, { useState, useEffect, useRef, useContext } from 'react';
import { UserPlusIcon, MagnifyingGlassIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { getConfig } from '../config/config-helper';
import { AuthContext } from 'react-oauth2-code-pkce/AuthContext';
import type { IAuthContext } from 'react-oauth2-code-pkce';
import { useAppSelector } from 'app/hooks';
import { handleInvite as handleInviteFunction } from './InviteColleagues/handleInvite';
import { sendInvitations as sendInvitationsFunction } from './InviteColleagues/sendInvitations';
import { CurrentUser } from './InviteColleagues/types';
import { selectEmail } from 'features/authentication/reducers/userSlice';
import { gql } from '@apollo/client';
import client from 'apolloClient';

interface InviteColleaguesButtonProps {
  groupId?: string | number;
}

const InviteColleaguesButton: React.FC<InviteColleaguesButtonProps> = ({ groupId }) => {
  const { token } = useContext<IAuthContext>(AuthContext);
  const userEmail = useAppSelector(selectEmail);
  const [currentUser, setCurrentUser] = useState<CurrentUser>({
    username: '',
    avatarUrl: '',
    emails: {
      nodes: [{ email: userEmail }],
    },
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [emails, setEmails] = useState('');
  const [accessLevel, setAccessLevel] = useState('developer');
  const [selectedGroupId, setSelectedGroupId] = useState(groupId !== undefined ? String(groupId) : '');
  const [availableAccessLevels, setAvailableAccessLevels] = useState<string[]>(['developer']);
  const [externalDomainWarning, setExternalDomainWarning] = useState<string | null>(null);
  const [showExternalConfirmation, setShowExternalConfirmation] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState<Array<{ email: string; accessLevel: string }>>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [invitedEmails, setInvitedEmails] = useState<
    Array<string | { email: string; access_level?: string; status?: string; gitlab_id?: string }>
  >([]);
  const [skippedUsers, setSkippedUsers] = useState<Array<{ email: string; reason?: string }>>([]);
  const [selectedGroupName, setSelectedGroupName] = useState('');
  const [availableGroups, setAvailableGroups] = useState<
    Array<{ id: string | number; name: string; accessLevel?: string }>
  >([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch current user data when token changes
  useEffect(() => {
    if (token !== '') {
      const GET_CURRENT_USER = gql`
        query {
          currentUser {
            username
            avatarUrl
            emails {
              nodes {
                email
              }
            }
            groupMemberships {
              nodes {
                group {
                  fullPath
                }
              }
            }
          }
        }
      `;

      client
        .query({
          query: GET_CURRENT_USER,
          context: { headers: { authorization: `Bearer ${token}` } },
        })
        .then((response) => {
          setCurrentUser(response.data.currentUser);
        })
        .catch((error) => {
          console.error('Error fetching current user:', error);
        });
    }
  }, [token]);

  // Fetch available groups when the modal opens
  useEffect(() => {
    if (isModalOpen) {
      void fetchAvailableGroups();
    }
  }, [isModalOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current !== null && !dropdownRef.current.contains(event.target as Node)) {
        setIsGroupDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Map GitLab access level numbers to our access level strings
  const mapGitLabAccessLevel = (level: number): string => {
    switch (level) {
      case 50:
        return 'owner';
      case 40:
        return 'maintainer';
      case 30:
        return 'developer';
      case 20:
        return 'reporter';
      case 10:
        return 'guest';
      default:
        return 'developer';
    }
  };

  const fetchAvailableGroups = async (): Promise<void> => {
    setIsLoadingGroups(true);
    setError(null);

    try {
      const { dataopsliveBaseUrl } = getConfig();
      let allGroups: Array<{ id: string | number; name: string; accessLevel: string }> = [];
      let page = 1;
      let hasMorePages = true;

      // Fetch all pages of groups
      while (hasMorePages) {
        const response = await axios.get(`${dataopsliveBaseUrl}/api/v4/groups`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          withCredentials: false,
          params: {
            min_access_level: 30, // Developer access or higher
            per_page: 100,
            page,
          },
        });

        const groups = response.data.map(
          (group: { id: string | number; name?: string; full_name?: string; access_level?: number }) => ({
            id: group.id,
            name: group.full_name ?? group.name ?? `Group ${group.id}`,
            accessLevel: mapGitLabAccessLevel(group.access_level ?? 30),
          }),
        );

        allGroups = [...allGroups, ...groups];

        // Check if there are more pages
        const totalPages = parseInt(response.headers['x-total-pages'] ?? '1', 10);
        hasMorePages = page < totalPages;
        page++;
      }

      // Set available groups
      setAvailableGroups(allGroups);

      // Set default access level to developer
      setAvailableAccessLevels(['guest', 'reporter', 'developer']);

      // Set first group as selected if there are any groups and no groupId was provided
      if (allGroups.length > 0 && selectedGroupId === '') {
        setSelectedGroupId(String(allGroups[0].id));
        setSelectedGroupName(allGroups[0].name);
      } else if (selectedGroupId !== '') {
        // If groupId was provided, find the group name
        const selectedGroup = allGroups.find((g) => String(g.id) === selectedGroupId);
        if (selectedGroup !== undefined) {
          setSelectedGroupName(selectedGroup.name);
        }
      }
    } catch (err) {
      console.error('Failed to fetch available groups:', err);
      setError('Failed to load groups. Please try again.');
      setAvailableGroups([]);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  // Update available access levels when group selection changes
  const handleGroupChange = (groupId: string): void => {
    const selectedGroup = availableGroups.find((g) => String(g.id) === groupId);
    if (selectedGroup !== undefined) {
      setSelectedGroupName(selectedGroup.name);
    }
    setSelectedGroupId(groupId);
  };

  // Wrapper for sendInvitations to pass all required parameters
  const sendInvitations = async (invitations: Array<{ email: string; accessLevel: string }>): Promise<void> => {
    await sendInvitationsFunction(
      invitations,
      selectedGroupId,
      currentUser,
      token,
      setIsSubmitting,
      setInvitedEmails,
      setSkippedUsers,
      setShowSuccessModal,
      setEmails,
      setExternalDomainWarning,
      setShowExternalConfirmation,
      setPendingInvitations,
      setError,
    );
  };

  // Wrapper for handleInvite to pass all required parameters
  const handleInvite = (): void => {
    handleInviteFunction(
      emails,
      setIsSubmitting,
      setError,
      currentUser,
      setExternalDomainWarning,
      setPendingInvitations,
      setShowExternalConfirmation,
      accessLevel,
      sendInvitations,
    );
  };

  // Handle confirmation of external domain invitations
  const handleConfirmExternalInvitations = (): void => {
    setShowExternalConfirmation(false);
    void sendInvitations(pendingInvitations);
  };

  // Handle cancellation of external domain invitations
  const handleCancelExternalInvitations = (): void => {
    setShowExternalConfirmation(false);
    setPendingInvitations([]);
    setExternalDomainWarning(null);
  };

  const openModal = (): void => {
    setIsModalOpen(true);
  };
  const closeSuccessModal = (): void => {
    // Close both the success modal and the main invitation modal
    setShowSuccessModal(false);
    setIsModalOpen(false);
    setInvitedEmails([]);
    setSkippedUsers([]);
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
    setShowExternalConfirmation(false);
    setPendingInvitations([]);
    setError(null);
    setExternalDomainWarning(null);
  };

  return (
    <>
      <button
        onClick={openModal}
        className="flex items-center gap-2 px-3 py-1.5 bg-dataops-yale-blue text-white rounded-md text-sm hover:bg-dataops-yale-blue/90 transition-colors"
        title="Invite colleagues to join this event"
      >
        <UserPlusIcon className="h-4 w-4" />
        Invite Colleagues
      </button>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-8 text-gray-700 text-center">
              Invite to DataOps.live group and projects
            </h2>

            {/* Green checkmark icon */}
            <div className="flex justify-center mb-4">
              <div className="bg-green-500 rounded-full p-4 w-20 h-20 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 text-white"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>

            <h3 className="text-lg font-medium mb-4 text-gray-700 text-center">Invite summary</h3>

            {/* Successful invitations */}
            {invitedEmails.length > 0 && (
              <div className="mb-6 text-left">
                <div className="flex items-center mb-2">
                  <div className="bg-green-100 rounded-full p-1 mr-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-green-600"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <h4 className="font-medium text-gray-700">
                    {invitedEmails.length} user{invitedEmails.length !== 1 ? 's' : ''} successfully invited
                  </h4>
                </div>
                <p className="mb-2 text-gray-600">
                  The following users have been emailed with a request to join{' '}
                  {typeof selectedGroupName === 'string' && selectedGroupName.trim() !== ''
                    ? selectedGroupName
                    : 'the group'}
                  :
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-4">
                  {invitedEmails.map((item, index) => (
                    <li key={index}>{typeof item === 'string' ? item : item.email}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Skipped users */}
            {skippedUsers.length > 0 && (
              <div className="mb-6 text-left">
                <div className="flex items-center mb-2">
                  <div className="bg-yellow-100 rounded-full p-1 mr-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-yellow-600"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <h4 className="font-medium text-gray-700">
                    {skippedUsers.length} user{skippedUsers.length !== 1 ? 's' : ''} skipped
                  </h4>
                </div>
                <p className="mb-2 text-gray-600">The following users could not be invited:</p>
                <ul className="list-none pl-0 text-gray-600 mb-4">
                  {skippedUsers.map((user, index) => (
                    <li key={index} className="mb-2 border-l-4 border-yellow-400 pl-3 py-1 bg-yellow-50">
                      <span className="font-medium">{user.email}</span>
                      {typeof user.reason === 'string' && user.reason.trim() !== '' && (
                        <div className="text-sm text-gray-500 mt-1">Reason: {user.reason}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="text-center">
              <button
                onClick={closeSuccessModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Modal */}
      {isModalOpen && !showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4 text-dataops-yale-blue">Invite Colleagues</h2>

            {error !== null && <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">{error}</div>}

            {showExternalConfirmation && externalDomainWarning !== null && (
              <div className="mb-4 border border-yellow-400 bg-yellow-50 rounded overflow-hidden">
                <div className="p-3 bg-yellow-100 text-yellow-800 font-medium flex items-start">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>External Domain Warning</span>
                </div>
                <div className="p-3">
                  <p className="mb-4">{externalDomainWarning}</p>
                  <p className="mb-4 text-sm">Are you sure you want to invite these external users?</p>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={handleCancelExternalInvitations}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmExternalInvitations}
                      className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors"
                    >
                      Yes, Invite External Users
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Group</label>
              <div className="relative" ref={dropdownRef}>
                <div
                  className="flex items-center justify-between w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-dataops-yale-blue cursor-pointer"
                  onClick={() => {
                    if (!isSubmitting && !isLoadingGroups) {
                      setIsGroupDropdownOpen(!isGroupDropdownOpen);
                    }
                  }}
                >
                  <div className="flex-grow truncate">
                    {isLoadingGroups
                      ? 'Loading groups...'
                      : typeof selectedGroupId === 'string' && selectedGroupId.trim() !== ''
                      ? availableGroups.find((g) => String(g.id) === selectedGroupId)?.name ?? 'Group'
                      : 'Select a group'}
                  </div>
                  <div className="flex-shrink-0 ml-2">
                    {isGroupDropdownOpen ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </div>
                </div>

                {isGroupDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
                    <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
                      <div className="relative">
                        <input
                          type="text"
                          value={groupSearchQuery}
                          onChange={(e) => {
                            setGroupSearchQuery(e.target.value);
                          }}
                          placeholder="Search groups..."
                          className="w-full p-2 pl-8 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-dataops-yale-blue"
                        />
                        <MagnifyingGlassIcon className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                      </div>
                    </div>

                    {availableGroups
                      .filter((group) => group.name.toLowerCase().includes(groupSearchQuery.toLowerCase()))
                      .map((group) => (
                        <div
                          key={group.id}
                          className={`p-2 hover:bg-gray-100 cursor-pointer ${
                            String(group.id) === selectedGroupId ? 'bg-gray-100' : ''
                          }`}
                          onClick={() => {
                            handleGroupChange(String(group.id));
                            setIsGroupDropdownOpen(false);
                          }}
                        >
                          {group.name}
                        </div>
                      ))}

                    {availableGroups.filter((group) =>
                      group.name.toLowerCase().includes(groupSearchQuery.toLowerCase()),
                    ).length === 0 && <div className="p-2 text-gray-500">No groups found</div>}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Addresses (comma separated)</label>
              <textarea
                value={emails}
                onChange={(e) => {
                  setEmails(e.target.value);
                  // Clear external domain warning and confirmation when editing emails
                  if (externalDomainWarning !== null || showExternalConfirmation) {
                    setExternalDomainWarning(null);
                    setShowExternalConfirmation(false);
                    setPendingInvitations([]);
                  }
                }}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-dataops-yale-blue"
                placeholder="email1@example.com, email2@example.com"
                rows={3}
                disabled={isSubmitting}
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Access Level</label>
              <select
                value={accessLevel}
                onChange={(e) => {
                  setAccessLevel(e.target.value);
                }}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-dataops-yale-blue"
                disabled={isSubmitting}
              >
                {availableAccessLevels.includes('guest') && <option value="guest">Guest</option>}
                {availableAccessLevels.includes('reporter') && <option value="reporter">Reporter</option>}
                {availableAccessLevels.includes('developer') && <option value="developer">Developer</option>}
                {availableAccessLevels.includes('maintainer') && <option value="maintainer">Maintainer</option>}
                {availableAccessLevels.includes('owner') && <option value="owner">Owner</option>}
              </select>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleInvite}
                className="px-4 py-2 rounded-md bg-dataops-secondary-blue px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-hover-secondary-blue disabled:bg-gray-400 flex items-center gap-2"
                disabled={
                  isSubmitting ||
                  typeof selectedGroupId !== 'string' ||
                  selectedGroupId.trim() === '' ||
                  showExternalConfirmation
                }
              >
                {isSubmitting ? 'Sending...' : 'Send Invitations'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InviteColleaguesButton;
